// src/components/features/chat/WelcomeBubble.jsx
//
// Sprint 13 v4 — Figma redesign: teal banner, SVG icons, new layout.
//
// Figma source: dark-take-47011126.figma.site (desktop + mobile frames)
//
// Changes from v3:
//   - Welcome area: white card bubble → full-width teal gradient banner
//   - "GOOD AFTERNOON" uppercase label above greeting
//   - SVG sparkle ✦ icon (replaces emoji bot avatar)
//   - Result card: SVG check-circle icon, teal "View →" text link, teal marker pills
//   - Stat widgets: clipboard/chart SVG icons, cleaner labels
//   - Health tip: pale yellow background, amber label
//   - "Now" timestamp after health tip
//   - No more bubble border-radius — full-width sections
//
// New color tokens used (with CSS fallbacks until added to index.css):
//   --teal:      #0d9488   chat primary accent
//   --teal-bg:   #f0fdfa   teal surface/pill bg
//   --teal-dark: #134e4a   banner gradient dark end
//   --yellow-bg: #fef9c3   health tip card
//
// Constitution §III: no clinical diagnosis wording. UNKNOWN → amber fallback.
// ⚠ Dr. Ajay sign-off required on all patient-facing copy.
//
// React expert review:
//   ✅ Pure presentational, zero side effects
//   ✅ type="button" on all interactive elements
//   ✅ aria-label on all buttons
//   ✅ Stable keys on .map()
//   ✅ No Tailwind, no MUI, no window.confirm()

import React from 'react';
import AssistantMark, { SparkleIcon } from './AssistantMark';
import Icon from '../../Icon';

// ─── Teal tokens (CSS var with fallback until index.css updated) ─────────────
const T = {
  teal:     'var(--teal)',
  tealDark: 'var(--teal-dark)',
  tealBg:   'var(--teal-bg)',
  tealMid:  'var(--teal-mid)',
  yellowBg: 'var(--yellow-bg, var(--amber-bg))',
  yellowLabel: 'var(--amber-text)',
};

// ─── SVG Icons ───────────────────────────────────────────────────────────────
function CheckCircleIcon({ size = 24, color }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="11" stroke={color} strokeWidth="2" fill={`${color}18`} />
      <path d="M8 12l3 3 5-5" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ClipboardIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  );
}

function ChartIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={T.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 20h18" />
      <path d="M7 16l4-8 4 4 4-8" />
    </svg>
  );
}

function LightbulbIcon({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={T.yellowLabel} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a7 7 0 014 12.7V17a1 1 0 01-1 1H9a1 1 0 01-1-1v-2.3A7 7 0 0112 2z" />
      <path d="M9 21h6" />
      <path d="M10 17v1" /><path d="M14 17v1" />
    </svg>
  );
}

// ─── Health tips ─────────────────────────────────────────────────────────────
const HEALTH_TIPS = [
  { tip: 'Drink a glass of water first thing in the morning. It kickstarts your metabolism and helps flush out overnight toxins.' },
  { tip: 'A 20-minute walk after your main meal helps steady blood sugar levels — especially helpful for South Asian metabolisms.' },
  { tip: 'Fenugreek (methi) seeds soaked overnight and taken in the morning may gently support insulin sensitivity over time.' },
  { tip: 'Consistent sleep timing matters as much as sleep duration. Try to wake at the same time every day — even on weekends.' },
  { tip: 'Three slow, deep belly breaths before a meal activates your parasympathetic system and supports better digestion.' },
  { tip: 'Eating raw salad before your cooked meal can reduce the glycaemic impact of the entire meal by up to 30%.' },
  { tip: '15–20 minutes of morning sunlight supports Vitamin D production and helps regulate your circadian rhythm.' },
  { tip: 'Even 10 minutes of gentle yoga or stretching in the morning can reduce cortisol and help manage inflammation.' },
];

function getDailyTip() {
  const start = new Date(new Date().getFullYear(), 0, 0);
  return HEALTH_TIPS[Math.floor((Date.now() - start) / 86400000) % HEALTH_TIPS.length];
}

// ─── Risk display ────────────────────────────────────────────────────────────
const RISK_DISPLAY = {
  LOW:      { label: 'Results look good',     color: T.teal },
  MODERATE: { label: 'A few things to check', color: 'var(--amber)' },
  HIGH:     { label: 'Needs attention',        color: 'var(--red)' },
  UNKNOWN:  { label: 'Review in progress',     color: 'var(--amber)' },
};

function getRiskDisplay(level) {
  const key = (level || 'UNKNOWN').toUpperCase();
  return RISK_DISPLAY[key] || RISK_DISPLAY.UNKNOWN;
}

function formatDate(d) {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return null; }
}

// ─── Result card ─────────────────────────────────────────────────────────────
function ResultCard({ result, onNavigate }) {
  if (!result) return null;
  const risk    = getRiskDisplay(result.consensusRiskLevel);
  const dateStr = formatDate(result.drawDate || result.createdAt);

  let elevatedMarkers = [];
  if (result.claudeRawResponse) {
    try {
      const raw = JSON.parse(result.claudeRawResponse);
      const mkrs = raw.markers || raw.sub_scores?.markers || [];
      elevatedMarkers = mkrs
        .filter(m => { const s = (m.status || '').toLowerCase(); return s === 'elevated' || s === 'high' || s === 'critical'; })
        .slice(0, 4);
    } catch { /* safe */ }
  }

  return (
    <button
      type="button"
      onClick={() => onNavigate('/patient/timeline')}
      aria-label="View your last lab results"
      style={{
        display: 'flex', flexDirection: 'column', gap: 10,
        background: 'var(--bg-surface)',
        border: '1px solid var(--bg-hover)',
        borderRadius: 12, padding: '14px 16px', width: '100%',
        cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircleIcon size={24} color={risk.color} />
          <div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.3 }}>
              {risk.label}
            </div>
            {dateStr && (
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-muted)', marginTop: 1 }}>
                {dateStr}
              </div>
            )}
          </div>
        </div>
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: T.teal }}>
          View →
        </span>
      </div>

      {/* Key findings */}
      {result.claudeKeyFindings && (
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)',
          lineHeight: 1.6, display: '-webkit-box', WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}>
          {result.claudeKeyFindings}
        </div>
      )}

      {/* Marker pills */}
      {elevatedMarkers.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {elevatedMarkers.map((m, i) => (
            <span key={m.name || i} style={{
              fontFamily: 'var(--font-body)', fontSize: 11, fontWeight: 500,
              padding: '3px 10px', borderRadius: 20,
              background: T.tealBg, color: T.teal,
              border: `1px solid ${T.teal}30`,
            }}>
              ↗ {m.name}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

// ─── Stat widget ─────────────────────────────────────────────────────────────
function StatWidget({ icon, label, value, subValue, valueColor, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${value}`}
      className="wb-stat"
      style={{
        display: 'flex', flexDirection: 'column', gap: 4,
        background: 'var(--bg-surface)',
        border: '1px solid var(--bg-hover)',
        borderRadius: 12, padding: '14px 16px',
        flex: '1 1 0', minWidth: 0, minHeight: 44,
        cursor: 'pointer', textAlign: 'left', transition: 'box-shadow 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        {icon}
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
      </div>
      <span style={{
        fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700,
        color: valueColor || 'var(--text-primary)', lineHeight: 1.2,
      }}>
        {value}
      </span>
      {subValue && (
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)' }}>
          {subValue}
        </span>
      )}
    </button>
  );
}

// ─── Health tip ──────────────────────────────────────────────────────────────
function HealthTipCard({ tip }) {
  return (
    <div style={{
      background: T.yellowBg, borderRadius: 12,
      padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-start',
    }}>
      <div style={{ marginTop: 2, flexShrink: 0 }}><LightbulbIcon /></div>
      <div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
          color: T.yellowLabel, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4,
        }}>
          Health Tip of the Day
        </div>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6 }}>
          {tip.tip}
        </div>
      </div>
    </div>
  );
}

// ─── Light-mode welcome message ─────────────────────────────────────────────
// Per the Figma reference for the (default) LIGHT assistant screen: one plain
// message bubble — greeting, then the invite-to-ask line — not the HEAVY
// mode's teal banner + separate result card + health-tip card.
//
// Used to have a middle sentence here summarizing the patient's last result
// or biological age (lightSummarySentence, since removed) — "A few things to
// check as of 21 Aug 2026." for a MODERATE-risk result, for instance. Dropped
// per explicit request: the risk-tier label read as an alarming, out-of-
// context fragment sitting on its own between two plain conversational
// lines, rather than something worth surfacing here at all.
function LightWelcomeMessage({ patient }) {
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = patient?.firstName || patient?.name?.split(' ')[0] || 'there';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <AssistantMark size={22} solid />
        <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 700, color: T.teal }}>
          Ask Previu
        </span>
      </div>
      <div style={{
        background: 'var(--bg-surface)', border: '1px solid var(--bg-hover)',
        borderRadius: 14, padding: '14px 16px',
      }}>
        <p style={{ margin: '0 0 10px', fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>
          {greeting}, {firstName}. Previu is here with you, every step of the way.
        </p>
        <p style={{ margin: 0, fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.6, color: 'var(--text-primary)' }}>
          Have questions about your results or what to do next? I'm right here — just ask.
        </p>
      </div>
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-muted)',
        marginTop: 8, display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <Icon name="clock" size={10} /> Now
      </div>
    </div>
  );
}

// ─── Skeleton ────────────────────────────────────────────────────────────────
function WelcomeSkeleton() {
  return (
    <div>
      <style>{`@keyframes wb-pulse{0%,100%{opacity:.55}50%{opacity:1}}`}</style>
      <div style={{ background: T.tealDark, borderRadius: 16, padding: '20px 20px 16px', marginBottom: 16 }}>
        {[100, 200, 280].map((w, i) => (
          <div key={i} style={{ height: 14, width: w, maxWidth: '90%', background: 'rgba(255,255,255,0.15)', borderRadius: 6, marginBottom: 10, animation: 'wb-pulse 1.4s ease-in-out infinite' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {[1, 2].map(i => <div key={i} style={{ flex: 1, height: 80, background: 'var(--bg-hover)', borderRadius: 12, animation: 'wb-pulse 1.4s ease-in-out infinite' }} />)}
      </div>
    </div>
  );
}

// ─── Main WelcomeBubble ──────────────────────────────────────────────────────
export default function WelcomeBubble({ patient, summaryData, onNavigate, isLightMode = true }) {
  if (!summaryData) return <WelcomeSkeleton />;

  // LIGHT (the default assistant screen, per the Figma reference) gets one
  // plain message bubble instead of HEAVY's richer banner + result card +
  // stat widgets + health-tip card — see LightWelcomeMessage's own comment.
  if (isLightMode) return <LightWelcomeMessage patient={patient} />;

  const { lastResult, openActionItems, totalActionItems, previuAge, previuAgeDelta } = summaryData;
  const tip      = getDailyTip();
  const hour     = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const firstName = patient?.firstName || patient?.name?.split(' ')[0] || 'there';

  return (
    <div>
      <style>{`
        .wb-stats-row { display: flex; gap: 10px; }
        .wb-banner { padding: 20px 20px 18px; }
        @media (max-width: 480px) {
          .wb-stats-row { flex-direction: column !important; }
          /* 18 Jul 2026 fix: .wb-stat's flex: '1 1 0' (see StatWidget below)
             was written for ROW mode — dividing available WIDTH equally
             among 2-3 side-by-side cards. Once flex-direction switches to
             column here, that SAME property divides available HEIGHT
             equally instead, and flex-basis:0 tells each card to ignore its
             own content height entirely — squeezing it to whatever sliver
             of height the equal split gives it. That's exactly why value
             text like "6 open" was overflowing outside its own card into
             the next one. flex:none lets each card size to its natural
             content height in column mode, like it should. */
          .wb-stat { flex: none !important; }
          .wb-banner { padding: 14px 14px 12px !important; }
          .wb-banner-heading { font-size: 18px !important; }
          .wb-banner-body { font-size: 12.5px !important; }
        }      `}</style>

      {/* ── Teal gradient banner ── */}
      <div className="wb-banner" style={{
        background: `linear-gradient(135deg, ${T.tealDark} 0%, ${T.tealMid} 100%)`,
        borderRadius: 16,
        position: 'relative', overflow: 'hidden', marginBottom: 12,
        transform: 'translateZ(0)', // Android Chrome: force GPU layer for clean border-radius on gradient
        WebkitTransform: 'translateZ(0)',
      }}>
        {/* Sparkle decoration */}
        <div style={{ position: 'absolute', top: 10, left: 14, opacity: 0.3 }}>
          <SparkleIcon size={16} color="var(--text-on-brand-dark)" />
        </div>

        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 10, fontWeight: 700,
          color: 'rgba(255,255,255,0.7)', letterSpacing: '0.1em',
          textTransform: 'uppercase', marginBottom: 4, paddingLeft: 2,
        }}>
          {greeting}
        </div>
        <div className="wb-banner-heading" style={{
          fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600,
          color: 'var(--text-on-brand-dark)', lineHeight: 1.3, marginBottom: 8,
        }}>
          Hello, {firstName} 👋
        </div>
        <div className="wb-banner-body" style={{
          fontFamily: 'var(--font-body)', fontSize: 13.5,
          color: 'rgba(255,255,255,0.85)', lineHeight: 1.6,
        }}>
          Previu is here with you, every step of the way. Here's a quick look at where things stand with your health today.
        </div>
      </div>

      {/* ── Result card ── */}
      {lastResult && (
        <div style={{ marginBottom: 10 }}>
          <ResultCard result={lastResult} onNavigate={onNavigate} />
        </div>
      )}

      {/* ── Stat widgets ── */}
      {/* Hidden in light mode (PatientSettingsPage's default assistant
          experience) — the care plan / bio age / lab results shortcuts
          duplicate what the quick-action buttons below already offer. */}
      {!isLightMode && (
        <div className="wb-stats-row" style={{ marginBottom: 20 }}>
          <StatWidget
            icon={<ClipboardIcon />}
            label="Care plan"
            value={
              openActionItems > 0
                ? `${openActionItems} open`
                : totalActionItems > 0
                  ? 'All done!'
                  : 'Not set up yet'
            }
            subValue={
              openActionItems > 0
                ? 'Tap to view'
                : totalActionItems > 0
                  ? 'Great progress 🎉'
                  : 'Your navigator will add steps soon'
            }
            valueColor={
              openActionItems > 0
                ? T.teal
                : totalActionItems > 0
                  ? 'var(--green)'
                  : 'var(--text-muted)'
            }
            onClick={() => onNavigate('/patient/care-plan')}
          />
          {previuAge != null && (
            <StatWidget
              icon={<ChartIcon />}
              label="Bio age"
              value={`${previuAge} yrs`}
              subValue={previuAgeDelta != null ? `${Number(previuAgeDelta) > 0 ? '+' : ''}${previuAgeDelta} vs calendar` : null}
              valueColor="var(--text-primary)"
              onClick={() => onNavigate('/patient/previu-age')}
            />
          )}
          {!lastResult && (
            <StatWidget
              icon={<ChartIcon />}
              label="Lab results"
              value="No tests yet"
              subValue="Book a lab test to get started"
              valueColor="var(--text-muted)"
              onClick={() => onNavigate('/patient/lab-tests')}
            />
          )}
        </div>
      )}

      {/* ── Invite text ── */}
      <p style={{
        fontFamily: 'var(--font-body)', fontSize: 13,
        color: 'var(--text-secondary)', margin: '0 0 12px 0', lineHeight: 1.6,
      }}>
        Have questions about your results or what to do next? I'm right here — just ask.
      </p>

      {/* ── Health tip ── */}
      <HealthTipCard tip={tip} />

      {/* ── Timestamp ── */}
      <div style={{
        fontFamily: 'var(--font-body)', fontSize: 11,
        color: 'var(--text-muted)', marginTop: 8, display: 'flex', alignItems: 'center', gap: 4,
      }}>
        <Icon name="clock" size={10} /> Now
      </div>
    </div>
  );
}
